import apiClient from './apiClient'; // Ensure this import is correct
import { ENDPOINTS } from './endpoints';

interface ShippingInfoResponse {
  msg: string;
  data: {
    id: string;
    first_name: string;
    last_name: string;
    address_line_1: string;
    address_line_2: string;
    email: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    phone_number: string;
  } | null;
  code: number | null;
  log: any | null;
}

interface ShippingInfoPayload {
  first_name: string;
  last_name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone_number: string;
}

interface ApiResponse {
  msg: string;
  code: number;
  data: {
    name: string;
    email: string;
    country_code: string;
    national_number: string;
    phone_number: string;
    shipping_address_line1: string;
    shipping_address_line2: string;
    shipping_city: string;
    shipping_state: string;
    shipping_zip: string;
  };
}

interface UserProfileResponse {
    msg: string;
    code: number;
    data: UserProfile;
  }

export const updateShippingInfo = async (shippingInfo: ShippingInfoPayload) => {
  try {
    // Get user data from localStorage for email
    const userData = localStorage.getItem('userData');
    let email = '';
    if (userData) {
      try {
        const user = JSON.parse(userData);
        email = user.email || '';
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    // Convert full country name to 2-letter ISO code (simplified approach)
    // In a real app, you should use a proper country code mapping library
    const getCountryCode = (country: string): string => {
      const countryMap: {[key: string]: string} = {
        'United States': 'US',
        'Canada': 'CA',
        'United Kingdom': 'GB',
        'Australia': 'AU',
        'Germany': 'DE',
        'France': 'FR',
        'Italy': 'IT',
        'Spain': 'ES',
        'Japan': 'JP',
        'China': 'CN',
        'India': 'IN',
        'Brazil': 'BR',
        'Mexico': 'MX',
        'South Korea': 'KR',
        // Add more as needed
      };
      
      return countryMap[country] || country.substring(0, 2).toUpperCase();
    };

    // Transform the payload to match what the API expects
    const apiPayload = {
      name: `${shippingInfo.first_name} ${shippingInfo.last_name}`,
      email: email,
      country_code: getCountryCode(shippingInfo.country),
      national_number: shippingInfo.phone_number,
      phone_number: shippingInfo.phone_number,
      shipping_address_line1: shippingInfo.address_line_1,
      shipping_address_line2: shippingInfo.address_line_2 || '',
      shipping_city: shippingInfo.city,
      shipping_state: shippingInfo.state,
      shipping_zip: shippingInfo.postal_code
    };
    
    const response = await apiClient.post(ENDPOINTS.USER_SHIPPING_INFO, apiPayload);
    return response.data.data;
  } catch (error) {
    console.error('Error updating shipping info:', error);
    throw error;
  }
};

export const fetchShippingInfo = async () => {
  try {
    const response = await apiClient.get<ShippingInfoResponse>(ENDPOINTS.USER_SHIPPING_INFO);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching shipping info:', error);
    // Don't throw error, just return null
    return null;
  }
};

interface UserProfile {
  username: string;
  email: string;
  bio: string;
}

export const fetchUserProfile = async (): Promise<UserProfile> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get<UserProfileResponse>(ENDPOINTS.USER_PROFILE, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    const { data } = response.data;
    return {
      username: data.username,
      email: data.email,
      bio: data.bio
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (profileData: UserProfile): Promise<UserProfile> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post<UserProfileResponse>(ENDPOINTS.USER_PROFILE, profileData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    const { data } = response.data;
    return {
      username: data.username,
      email: data.email,
      bio: data.bio
    };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};
